import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { isDtoBasketPlaced } from '@epicuro-next/data/baskets-grouping';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { CBasketService } from '../services';
import {
  loadDtoBasketFailure,
  loadDtoBasketSuccess,
  setBasketLoading,
} from '../state/actions';
import { selectBasketDtoFromEntitiesById } from '../state/selectors';

/**
 * Parameter key for the account
 */
export const BASKET_ID = 'basketId';

@Injectable()
export class ThankYouPageGuard {
  constructor(
    private router: Router,
    private cbasketService: CBasketService,
    private store: Store,
  ) {}

  public canActivate(
    routeSnapshot: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> {
    const basketId = routeSnapshot.params[BASKET_ID];

    return this.redirectWithDtoBasket(basketId);
  }

  private redirectWithDtoBasket(basketId: string) {
    return this.store
      .pipe(select(selectBasketDtoFromEntitiesById(basketId)), take(1))
      .pipe(
        switchMap((basket) => {
          if (basket) {
            return isDtoBasketPlaced(basket)
              ? of(true)
              : of(
                  this.router.createUrlTree([
                    APP_PRIVATE_PATHS.basket.path,
                    basketId,
                  ]),
                );
          }

          this.store.dispatch(setBasketLoading({ value: true }));

          return this.cbasketService.getDtoBasket(basketId).pipe(
            tap((dtoBasket) =>
              this.store.dispatch(loadDtoBasketSuccess({ basket: dtoBasket })),
            ),
            map((basketDto) =>
              isDtoBasketPlaced(basketDto)
                ? true
                : this.router.createUrlTree([
                    APP_PRIVATE_PATHS.basket.path,
                    basketId,
                  ]),
            ),
            catchError((error) => {
              this.store.dispatch(
                loadDtoBasketFailure({ error: error.message }),
              );
              return of(this.router.createUrlTree(['/']));
            }),
            finalize(() => {
              this.store.dispatch(setBasketLoading({ value: false }));
            }),
          );
        }),
      );
  }
}
